import React, { useRef, useEffect, useLayoutEffect, useState, useCallback, memo } from 'react';
import { getGradientColors } from '../utils/colorUtils';
import { formatLocalDate } from '../utils/dateHelpers';
import { LAYOUT_TYPES } from '../constants/layoutConfigs';
import { getWeekStart } from '../utils/weekCalculations';
import { useAnimations } from '../context/AnimationContext';

// Expose configuration to window for console adjustments
if (typeof window !== 'undefined') {
  window.gradientConfig = window.gradientConfig || {
    pointOffset: 0,              // Distance from row center (0-0.5, where 0.5 = row edge)
    curveTension: 0.8,           // Curve roundness (0-0.5, higher = rounder)
    fillOpacity: 0.95,           // Fill area opacity (0-1)
    mainLineOpacity: 0.01,       // Main line opacity (0-1)
    outerGlowOpacity: 0.1,       // Outer glow opacity (0-1)
    outerGlowBlur: 0,            // Blur amount for outer glow in pixels (0 = no blur)
    outerGlowStrokeWidth: 3,     // Stroke width for outer glow
    vectorEffect: 'none',        // 'non-scaling-stroke' or 'none' - controls stroke scaling behavior
    animationSpeed: 0.03,        // How fast the overlay animates to new positions (0-1, higher = faster)
  };
}

// Pure path computation. Called from both React render (via useLayoutEffect)
// and the ref-based wave-breathing rAF loop. No React state, no refs touched
// internally — all inputs flow through `ctx`. Keeping this pure is what
// lets the wave breathe at 60Hz without triggering React commits.
function computeFillPath(time, ctx) {
  const { habits, animatedProgress, rowGeom, minX, config } = ctx;
  if (habits.length === 0) return '';

  const useMeasured = rowGeom.rowCenters.length === habits.length;
  const fallbackRowHeight = 1000 / habits.length;
  const points = [];
  const waveAmplitude = 0.5;
  const waveFrequency = 1.5;
  const wavePhase = 0.5;

  habits.forEach((habit, habitIndex) => {
    const progressPercent = animatedProgress[habit.id] ?? 0;
    const effectiveX = minX + (progressPercent / 100) * (100 - minX);
    const rowCenter = useMeasured
      ? rowGeom.rowCenters[habitIndex]
      : habitIndex * fallbackRowHeight + fallbackRowHeight / 2;
    const rowTop = useMeasured
      ? (habitIndex === 0 ? 0 : rowGeom.boundaries[habitIndex - 1])
      : habitIndex * fallbackRowHeight;
    const rowBot = useMeasured
      ? (habitIndex === habits.length - 1 ? 1000 : rowGeom.boundaries[habitIndex])
      : (habitIndex + 1) * fallbackRowHeight;
    const offset = (rowBot - rowTop) * config.pointOffset;
    const horizontalShift = Math.sin(time * waveFrequency + habitIndex * wavePhase) * waveAmplitude;
    const yEntry = habitIndex === 0 ? 0 : rowCenter - offset;
    const yExit = habitIndex === habits.length - 1 ? 1000 : rowCenter + offset;
    points.push({ x: effectiveX + horizontalShift, y: yEntry });
    points.push({ x: effectiveX + horizontalShift, y: yExit });
  });

  let pathData = `M 0 0 L ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const yDiff = curr.y - prev.y;
    const cp1Y = prev.y + yDiff * config.curveTension;
    const cp2Y = curr.y - yDiff * config.curveTension;
    pathData += ` C ${prev.x} ${cp1Y}, ${curr.x} ${cp2Y}, ${curr.x} ${curr.y}`;
  }
  const lastPoint = points[points.length - 1];
  pathData += ` L ${lastPoint.x} 1000 L 0 1000 Z`;
  return pathData;
}

function computeWavePath(time, ctx) {
  const { habits, animatedProgress, rowGeom, minX, config } = ctx;
  if (habits.length === 0) return '';

  const useMeasured = rowGeom.rowCenters.length === habits.length;
  const fallbackRowHeight = 1000 / habits.length;
  const points = [];
  const waveAmplitude = 15;
  const waveFrequency = 1.5;
  const wavePhase = 0.5;

  habits.forEach((habit, habitIndex) => {
    const progressPercent = animatedProgress[habit.id] ?? 0;
    const effectiveX = minX + (progressPercent / 100) * (100 - minX);
    const rowCenter = useMeasured
      ? rowGeom.rowCenters[habitIndex]
      : habitIndex * fallbackRowHeight + fallbackRowHeight / 2;
    const rowTop = useMeasured
      ? (habitIndex === 0 ? 0 : rowGeom.boundaries[habitIndex - 1])
      : habitIndex * fallbackRowHeight;
    const rowBot = useMeasured
      ? (habitIndex === habits.length - 1 ? 1000 : rowGeom.boundaries[habitIndex])
      : (habitIndex + 1) * fallbackRowHeight;
    const offset = (rowBot - rowTop) * config.pointOffset;
    const verticalShift = Math.sin(time * waveFrequency + habitIndex * wavePhase) * waveAmplitude;
    const yEntry = (habitIndex === 0 ? 0 : rowCenter - offset) + verticalShift;
    const yExit = (habitIndex === habits.length - 1 ? 1000 : rowCenter + offset) + verticalShift;
    points.push({ x: effectiveX, y: yEntry });
    points.push({ x: effectiveX, y: yExit });
  });

  let pathData = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const yDiff = curr.y - prev.y;
    const cp1Y = prev.y + yDiff * config.curveTension;
    const cp2Y = curr.y - yDiff * config.curveTension;
    pathData += ` C ${prev.x} ${cp1Y}, ${curr.x} ${cp2Y}, ${curr.x} ${curr.y}`;
  }
  return pathData;
}

function GradientProgressOverlay({ habits, weekDates, completions, sickWeeks = {}, isMobile, layoutMode = LAYOUT_TYPES.DESKTOP_LANDSCAPE }) {
  const { reduceAnimations, perfTier } = useAnimations();
  // SMIL <animate> on <linearGradient> stops keeps the compositor busy on
  // every frame on low/mid Android WebViews. Only run them on high tier
  // — on lower tiers the gradient still renders, it just doesn't breathe.
  const smilEnabled = !reduceAnimations && perfTier === 'high';

  const config = typeof window !== 'undefined' ? window.gradientConfig : {
    pointOffset: 0,
    curveTension: 0.8,
    fillOpacity: 0.95,
    mainLineOpacity: 0.01,
    outerGlowOpacity: 0.1,
    outerGlowBlur: 0,
    outerGlowStrokeWidth: 3,
    vectorEffect: 'none',
    animationSpeed: 0.03,
  };

  // Layout-specific configurations
  const layoutConfigs = {
    [LAYOUT_TYPES.MOBILE_PORTRAIT]: {
      leftOffset: '-0.75rem',
      width: 'calc(100% + 1.5rem)',
      borderRadius: '0.5rem',
      strokeWidth: 0.6,
      opacity: 0.85
    },
    [LAYOUT_TYPES.MOBILE_LANDSCAPE]: {
      leftOffset: '-0.75rem',
      width: 'calc(100% + 1.5rem)',
      borderRadius: '0.625rem',
      strokeWidth: 0.7,
      opacity: 0.9
    },
    [LAYOUT_TYPES.TABLET_PORTRAIT]: {
      leftOffset: '-1.5rem',
      width: 'calc(100% + 3rem)',
      borderRadius: '1.5rem',
      strokeWidth: 0.8,
      opacity: 0.95
    },
    [LAYOUT_TYPES.TABLET_LANDSCAPE]: {
      leftOffset: '-1.5rem',
      width: 'calc(100% + 3rem)',
      borderRadius: '2.5rem',
      strokeWidth: 1,
      opacity: 0.95
    },
    [LAYOUT_TYPES.DESKTOP_PORTRAIT]: {
      leftOffset: '-2rem',
      width: 'calc(100% + 4rem)',
      borderRadius: '3rem',
      strokeWidth: 1.2,
      opacity: 1
    },
    [LAYOUT_TYPES.DESKTOP_LANDSCAPE]: {
      leftOffset: '-2rem',
      width: 'calc(100% + 4rem)',
      borderRadius: '3rem',
      strokeWidth: 1.2,
      opacity: 1
    }
  };

  const layoutConfig = layoutConfigs[layoutMode] || layoutConfigs[LAYOUT_TYPES.DESKTOP_LANDSCAPE];

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  // Measured row geometry, in SVG-y units (0..1000), aligned to the wrapper.
  // `rowCenters[i]` is the y-center of habit row i; `boundaries[i]` is the
  // midpoint of the gap between row i and row i+1 (used for gradient stops).
  // Empty until the first layout measurement; the path falls back to the
  // uniform-division math when empty, so the first paint isn't broken.
  const [rowGeom, setRowGeom] = useState({ rowCenters: [], boundaries: [] });

  // Path elements — the wave-rAF loop and useLayoutEffect write the `d`
  // attribute imperatively. The JSX renders `d=""` and never participates
  // in path math, so progress lerping and wave breathing never trigger a
  // React commit. The only React renders happen when habits/rowGeom/etc.
  // change (gradient stops + structural pieces re-render).
  const fillPathRef = useRef(null);
  const glowPathRef = useRef(null);
  const mainPathRef = useRef(null);

  // Progress lives in refs, not React state. The lerp rAF mutates
  // `animatedProgressRef.current` directly; the wave-paint rAF reads it.
  // No React commits per frame — that was the original perf bug.
  const animatedProgressRef = useRef({});
  const targetProgressRef = useRef({});
  const lerpRunningRef = useRef(false);
  const lerpFrameRef = useRef(null);

  // Get week start string for sick weeks lookup
  const weekStartStr = weekDates.length > 0 ? formatLocalDate(getWeekStart(new Date(weekDates[0]))) : null;

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Measure the real row positions so the wave/gradient line up with the
  // actual rendered rows. The wrapper is absolutely-positioned inside the
  // habit-row container; its siblings are the rows themselves (tagged with
  // `data-habit-row`). We translate each row's top/bottom into wrapper-local
  // pixels and then into SVG-y (0..1000). This replaces the old assumption
  // that 1000 SVG units are split uniformly across `habits.length` rows,
  // which ignored both the wrapper's -0.75rem top/bottom bleed and the
  // `space-y-*` gaps between rows.
  useLayoutEffect(() => {
    const measure = () => {
      const wrapper = containerRef.current;
      if (!wrapper) return;
      const parent = wrapper.parentElement;
      if (!parent) return;
      const rows = parent.querySelectorAll('[data-habit-row]');
      if (rows.length === 0) {
        setRowGeom(prev => (prev.rowCenters.length === 0 ? prev : { rowCenters: [], boundaries: [] }));
        return;
      }

      const wrapperRect = wrapper.getBoundingClientRect();
      const wrapperTop = wrapperRect.top;
      const wrapperH = wrapperRect.height;
      if (wrapperH <= 0) return;

      const rowTops = [];
      const rowBottoms = [];
      rows.forEach(row => {
        const r = row.getBoundingClientRect();
        rowTops.push(r.top - wrapperTop);
        rowBottoms.push(r.bottom - wrapperTop);
      });

      const toSvg = px => (px / wrapperH) * 1000;
      const rowCenters = rowTops.map((t, i) => toSvg((t + rowBottoms[i]) / 2));
      const boundaries = [];
      for (let i = 0; i < rowTops.length - 1; i++) {
        boundaries.push(toSvg((rowBottoms[i] + rowTops[i + 1]) / 2));
      }

      setRowGeom(prev => {
        if (
          prev.rowCenters.length === rowCenters.length &&
          prev.boundaries.length === boundaries.length &&
          prev.rowCenters.every((v, i) => Math.abs(v - rowCenters[i]) < 0.01) &&
          prev.boundaries.every((v, i) => Math.abs(v - boundaries[i]) < 0.01)
        ) {
          return prev;
        }
        return { rowCenters, boundaries };
      });
    };

    measure();

    const wrapper = containerRef.current;
    const parent = wrapper && wrapper.parentElement;
    if (!parent) return;

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(parent);
      parent.querySelectorAll('[data-habit-row]').forEach(row => ro.observe(row));
    }

    const mo = new MutationObserver(measure);
    mo.observe(parent, { childList: true, subtree: false });

    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [habits.length, layoutMode]);

  // Calculate the minimum X position (0% progress) to cover habit names
  const getMinX = () => {
    if (isMobile || !containerWidth) return 0;
    const leftOffsetRem = parseFloat(layoutConfig.leftOffset) || 0;
    const leftOffsetPx = Math.abs(leftOffsetRem * 16);
    const habitColumnWidth = 180;
    const gap = 16;
    const targetPixelWidth = habitColumnWidth + (gap / 2) + leftOffsetPx;
    return (targetPixelWidth / containerWidth) * 100;
  };

  const minX = getMinX();

  // Snapshot of everything the rAF loop needs. Updated on every render
  // (synchronously, during render — so it's correct by the time
  // useLayoutEffect or the next rAF tick reads it).
  const pathStateRef = useRef(null);
  pathStateRef.current = { habits, rowGeom, minX, config, animatedProgressRef };

  // Imperative paint: read state from refs, compute paths, write via
  // setAttribute. Called once after every React commit (useLayoutEffect)
  // and on every animation frame by the wave-rAF loop.
  const paintOnce = useCallback((time) => {
    const state = pathStateRef.current;
    if (!state || state.habits.length === 0) return;
    const t = time !== undefined ? time : (reduceAnimations ? 0 : Date.now() / 1000);
    const ctx = {
      habits: state.habits,
      animatedProgress: state.animatedProgressRef.current,
      rowGeom: state.rowGeom,
      minX: state.minX,
      config: state.config,
    };
    if (fillPathRef.current) fillPathRef.current.setAttribute('d', computeFillPath(t, ctx));
    const wave = computeWavePath(t, ctx);
    if (glowPathRef.current) glowPathRef.current.setAttribute('d', wave);
    if (mainPathRef.current) mainPathRef.current.setAttribute('d', wave);
  }, [reduceAnimations]);

  // Paint synchronously after every commit. Bridges the gap between a
  // React render (which writes `d=""` placeholders) and the next rAF tick
  // so there's no empty-path flash.
  useLayoutEffect(() => {
    paintOnce();
  });

  // Persistent wave-breathing loop. Runs at 60Hz with zero React commits
  // per frame — `setAttribute` writes the new `d` directly to the DOM.
  // When `reduceAnimations` is on, the wave doesn't breathe (per design);
  // useLayoutEffect handles paints on data changes.
  //
  // On perf-low, throttle to 30Hz by skipping every other rAF tick. The
  // wave still breathes visibly; the cost halves. The browser still
  // composites the SVG every vsync (60Hz), but the *d-attribute change*
  // (which triggers re-rasterization of the path) only happens 30×/sec.
  useEffect(() => {
    if (reduceAnimations) return;
    let frameId;
    let frameCount = 0;
    const throttled = perfTier === 'low';
    const tick = () => {
      if (!throttled || (frameCount++ & 1) === 0) paintOnce();
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [reduceAnimations, perfTier, paintOnce]);

  // Lerp loop — only running while progress is actually interpolating.
  // Self-cancels after 3 idle frames so the device fully idles between
  // toggles instead of churning a setState every frame forever.
  //
  // On perf-low: tick at 30Hz (skip every other rAF) AND double the
  // interpolation step so settling time stays the same. The wave's own
  // throttle already halved its read rate, so updating animated progress
  // any faster than that is wasted work.
  const startLerp = useCallback(() => {
    if (lerpRunningRef.current || reduceAnimations) return;
    lerpRunningRef.current = true;
    const throttled = perfTier === 'low';
    const stepScale = throttled ? 2 : 1;
    let idle = 0;
    let frameCount = 0;
    const advance = () => {
      const targets = targetProgressRef.current;
      const prev = animatedProgressRef.current;
      const next = { ...prev };
      let hasChanges = false;
      Object.keys(targets).forEach(id => {
        const t = targets[id];
        const c = prev[id];
        if (c === undefined) {
          next[id] = t;
          hasChanges = true;
        } else if (Math.abs(c - t) > 0.01) {
          next[id] = c + (t - c) * config.animationSpeed * stepScale;
          hasChanges = true;
        } else if (c !== t) {
          next[id] = t;
          hasChanges = true;
        }
      });
      if (hasChanges) animatedProgressRef.current = next;
      return hasChanges;
    };
    const tick = () => {
      // Throttle: only do real work on every-other frame on perf-low.
      const doWork = !throttled || (frameCount++ & 1) === 0;
      const hasChanges = doWork ? advance() : true; // hold idle counter while skipping
      if (doWork && !hasChanges) {
        if (++idle >= 3) {
          lerpRunningRef.current = false;
          lerpFrameRef.current = null;
          return;
        }
      } else if (doWork) {
        idle = 0;
      }
      lerpFrameRef.current = requestAnimationFrame(tick);
    };
    lerpFrameRef.current = requestAnimationFrame(tick);
  }, [reduceAnimations, perfTier, config.animationSpeed]);

  // Cancel any in-flight lerp on unmount.
  useEffect(() => {
    return () => {
      if (lerpFrameRef.current) cancelAnimationFrame(lerpFrameRef.current);
      lerpRunningRef.current = false;
    };
  }, []);

  // Update target progress when completions or sick weeks change.
  // Triggers the lerp (or sets values directly when reduced animations).
  useEffect(() => {
    const targets = {};
    habits.forEach(habit => {
      const isSick = weekStartStr && sickWeeks[`${habit.id}-${weekStartStr}`];
      if (isSick) {
        targets[habit.id] = 100;
      } else {
        const weekCount = weekDates.filter(date => completions[`${habit.id}-${date}`]).length;
        targets[habit.id] = (weekCount / habit.weekly_goal) * 100;
      }
    });
    targetProgressRef.current = targets;

    if (reduceAnimations) {
      animatedProgressRef.current = { ...targets };
      paintOnce();
    } else {
      // Seed any habit we haven't seen before so the first lerp tick has
      // a baseline (without this, the wave would briefly snap from 0).
      Object.keys(targets).forEach(id => {
        if (animatedProgressRef.current[id] === undefined) {
          animatedProgressRef.current[id] = targets[id];
        }
      });
      startLerp();
    }
  }, [habits, weekDates, completions, sickWeeks, weekStartStr, reduceAnimations, startLerp, paintOnce]);

  const gradientId = `gradient-${layoutMode}-${isMobile ? 'mobile' : 'desktop'}-vertical`;

  // Horizontal fade: the original implementation defined an SVG <mask>
  // (with a horizontal linearGradient) and applied it to the fill path.
  // SVG <mask> forces a separate raster buffer per paint, which
  // dominated the residual paint cost (~80% of CPU was browser-side
  // paint after Phase 4). CSS mask-image on the wrapper composites for
  // free at the GPU layer and matches the previous look 1:1. Side
  // effect: the (near-invisible) glow + main line strokes now also
  // fade out on the right edge, where before they kept full opacity —
  // imperceptible given mainLineOpacity=0.01, outerGlowOpacity=0.1.
  const wrapperFadeMask = 'linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 70%, rgba(0,0,0,0.2) 95%, transparent 100%)';
  // Vertical bleed: how far the overlay extends above the first row / below the
  // last. Originally hardcoded to '-0.75rem' (12 px) for a soft aesthetic bleed,
  // but in tight phone-landscape the bleed visibly overlaps the day-labels row
  // and (when content barely fits) gets clipped by overflow-y-hidden — both look
  // worse than the bleed reads as polish, so zero it on landscape layouts.
  const verticalBleed =
    layoutMode === LAYOUT_TYPES.MOBILE_LANDSCAPE ||
    layoutMode === LAYOUT_TYPES.TABLET_LANDSCAPE
      ? '0'
      : '-0.75rem';

  const wrapperStyle = {
    zIndex: 20,
    left: layoutConfig.leftOffset,
    width: layoutConfig.width,
    top: verticalBleed,
    bottom: verticalBleed,
    borderRadius: layoutConfig.borderRadius,
    overflow: 'hidden',
    position: 'absolute',
    maskImage: wrapperFadeMask,
    WebkitMaskImage: wrapperFadeMask,
  };

  const mainLineStrokeWidth = layoutConfig.strokeWidth;

  return (
    <div ref={containerRef} style={wrapperStyle} className="pointer-events-none">
      <svg
        className="w-full h-full overflow-visible"
        viewBox="0 0 100 1000"
        preserveAspectRatio="none"
      >
        <defs>
          {/* Create mesh gradient with all habit colors fading vertically.
              SVG SMIL <animate> keeps the compositor busy every frame on
              old Chromiums — the `reduce-animations` CSS class only affects
              CSS animations, not SMIL. Gate them on `reduceAnimations` so
              low-end devices (Android 9 / WebView 77 tablet) actually idle. */}
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            {smilEnabled && (
              <>
                <animate attributeName="y1" values="0%;5%;0%" dur="10s" repeatCount="indefinite" />
                <animate attributeName="y2" values="100%;105%;100%" dur="10s" repeatCount="indefinite" />
              </>
            )}
            {habits.map((habit, habitIndex) => {
              const colors = getGradientColors(habit.color);
              // Use measured boundaries when available; fall back to uniform
              // division on the very first paint before layout measurement.
              const useMeasured = rowGeom.boundaries.length === habits.length - 1;
              const startPos = useMeasured
                ? (habitIndex === 0 ? 0 : rowGeom.boundaries[habitIndex - 1] / 10)
                : habitIndex * (100 / habits.length);
              const endPos = useMeasured
                ? (habitIndex === habits.length - 1 ? 100 : rowGeom.boundaries[habitIndex] / 10)
                : (habitIndex + 1) * (100 / habits.length);
              const rowSpan = endPos - startPos;
              const transitionZone = rowSpan * 0.3; // 30% of this row's span

              return (
                <React.Fragment key={`vert-${habit.id}`}>
                  {/* Transition from previous color */}
                  {habitIndex > 0 && (
                    <stop
                      offset={`${Math.max(0, startPos - transitionZone / 2)}%`}
                      stopColor={`rgba(${getGradientColors(habits[habitIndex - 1].color).from}, 0.6)`}
                    >
                      {smilEnabled && (
                        <animate attributeName="stop-opacity" values="0.6;0.8;0.6" dur="4s" repeatCount="indefinite" />
                      )}
                    </stop>
                  )}
                  {/* This habit's color starts */}
                  <stop
                    offset={`${startPos + transitionZone / 2}%`}
                    stopColor={`rgba(${colors.from}, 0.6)`}
                  >
                    {smilEnabled && (
                      <animate attributeName="stop-opacity" values="0.6;0.9;0.6" dur="4s" repeatCount="indefinite" />
                    )}
                  </stop>
                  {/* This habit's color holds in the middle */}
                  <stop
                    offset={`${endPos - transitionZone / 2}%`}
                    stopColor={`rgba(${colors.from}, 0.6)`}
                  >
                    {smilEnabled && (
                      <animate attributeName="stop-opacity" values="0.6;0.9;0.6" dur="4s" repeatCount="indefinite" />
                    )}
                  </stop>
                </React.Fragment>
              );
            })}
          </linearGradient>

          {/* Horizontal fade used to be an SVG <mask> here — now handled
              by `maskImage` on the wrapper div for cheaper compositing. */}
        </defs>

        {habits.length > 0 && (
          <>
            {/* Filled area that follows the curve. `d` is written
                imperatively by paintOnce / the wave-rAF loop. */}
            <path
              ref={fillPathRef}
              d=""
              fill={`url(#${gradientId})`}
              opacity={layoutConfig.opacity * config.fillOpacity}
            />
            {/* Outer glow layer */}
            <path
              ref={glowPathRef}
              d=""
              stroke={`rgba(255, 255, 255, ${config.outerGlowOpacity})`}
              strokeWidth={config.outerGlowStrokeWidth}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect={config.vectorEffect}
              style={{ filter: config.outerGlowBlur > 0 ? `blur(${config.outerGlowBlur / 16}rem)` : 'none' }}
            />
            {/* Main line */}
            <path
              ref={mainPathRef}
              d=""
              stroke={`rgba(255, 255, 255, ${config.mainLineOpacity})`}
              strokeWidth={mainLineStrokeWidth}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect={config.vectorEffect}
            />
          </>
        )}
      </svg>
    </div>
  );
}

// Custom comparator: `completions` gets a fresh object identity on every
// tap (optimistic update spreads the whole map), but the overlay only
// depends on entries for the visible week. Skip re-render when nothing in
// `habits × weekDates` actually changed — this keeps unrelated App state
// updates (streak recompute, hover state, etc.) from re-running the path
// math and repainting the SVG.
const MemoizedGradientProgressOverlay = memo(GradientProgressOverlay, (prev, next) => {
  if (prev.isMobile !== next.isMobile) return false;
  if (prev.layoutMode !== next.layoutMode) return false;
  if (prev.habits !== next.habits) return false;
  if (prev.weekDates !== next.weekDates) return false;
  if (prev.sickWeeks !== next.sickWeeks) return false;
  if (prev.completions === next.completions) return true;
  for (const habit of next.habits) {
    for (const d of next.weekDates) {
      const k = `${habit.id}-${d}`;
      if (prev.completions[k] !== next.completions[k]) return false;
    }
  }
  return true;
});

// Thin gate so we can early-return when the user has Reduce Animations on.
// Putting the check inside the main component would violate Rules of Hooks
// (the body calls useState/useEffect/useRef after it). Even with SMIL gated
// off, the static SVG here paints a mask + 15-stop gradient + 3 cubic-Bezier
// paths on every tap — ~100ms on low-end tablets. Users who enabled the
// setting chose perf; we drop the polish entirely for them.
export default function GradientProgressOverlayGate(props) {
  const { reduceAnimations } = useAnimations();
  if (reduceAnimations) return null;
  return <MemoizedGradientProgressOverlay {...props} />;
}
